import Image from '../../core/Image';
import Text from '../../core/Text';
import View from '../../core/View';

type UploadValue = { objectUrl?: string | null } | null;
type LinkValue = {
  linkMobile?: string | null;
  linkPc?: string | null;
  linkAndroid?: string | null;
  linkIos?: string | null;
};
type ButtonValue = LinkValue & { name?: string | null; linkType?: string | null };
type CouponValue = LinkValue & { title?: string | null; description?: string | null };
type CommerceValue = {
  title?: string | null;
  regularPrice?: number | null;
  discountPrice?: number | null;
  discountRate?: number | null;
  discountFixed?: number | null;
  // 고정변수명 조합 — 저장된 템플릿은 숫자 필드가 비고 *Name 에 변수명이 들어온다.
  regularPriceName?: string | null;
  discountPriceName?: string | null;
  discountRateName?: string | null;
  discountFixedName?: string | null;
  // 작성 중(저장 전) 폼 값 — 서버가 채우기 전이라 *Name 이 아직 없다.
  usePriceVariable?: boolean | null;
};
type CarouselItemValue = {
  header?: string | null;
  content?: string | null;
  additionalContent?: string | null;
  image?: UploadValue;
  imageUrl?: string | null;
  imageLink?: string | null;
  commerce?: CommerceValue | null;
  buttons?: ReadonlyArray<ButtonValue> | null;
  coupon?: CouponValue | null;
};
type WideItemValue = LinkValue & {
  title?: string | null;
  image?: UploadValue;
  imageUrl?: string | null;
};

export type BrandMessageTemplatePreviewValue = {
  chatBubbleType?: string | null;
  content?: string | null;
  header?: string | null;
  additionalContent?: string | null;
  image?: UploadValue;
  imageUrl?: string | null;
  buttons?: ReadonlyArray<ButtonValue> | null;
  coupon?: CouponValue | null;
  commerce?: CommerceValue | null;
  mainWideItem?: WideItemValue | null;
  subWideItemList?: ReadonlyArray<WideItemValue> | null;
  wideItemList?: ReadonlyArray<WideItemValue> | null;
  carousel?: {
    head?: (LinkValue & CarouselItemValue) | null;
    list?: ReadonlyArray<CarouselItemValue> | null;
    tail?: LinkValue | null;
  } | null;
  video?: (UploadValue & { thumbnailUrl?: string | null; videoUrl?: string | null; vid?: string | null }) | null;
};

type Props = { value: BrandMessageTemplatePreviewValue };

const PROFILE_IMAGE =
  'https://play-lh.googleusercontent.com/Q9NkwDb-oR-rlbs_wYahWFP5HcfRCzXN-btp4z9k52fDlbe3RbCcIR_8bTKHwZtBn4c=s96-rw';

const assetUrl = (image?: UploadValue, imageUrl?: string | null) => image?.objectUrl || imageUrl || '';

const formatPrice = (value?: number | null) => (value == null ? '' : value.toLocaleString());

// 서버(brandmessages/graphql/template_payload.py COMMERCE_PRICE_VARIABLES)와 같은 값.
const COMMERCE_PRICE_VARIABLES = {
  regularPrice: '#{정상가격}',
  discountPrice: '#{할인가격}',
  discountRate: '#{할인율}',
  discountFixed: '#{정액할인가격}',
} as const;

// 빈 템플릿도 형태가 보이도록 자리표시 문구를 넣는다(유형 선택 직후 미리보기가 비어 보이던 문제).
const PLACEHOLDER_BG = '#f0f0f0';
const PLACEHOLDER_FG = '#b0b4ba';
const PREVIEW_PLACEHOLDER = {
  image: '이미지를 등록해주세요.',
  content: '내용 미리보기 입니다.',
  header: '제목 미리보기 입니다.',
  itemTitle: '타이틀 미리보기 입니다.',
  commerceTitle: '상품명 미리보기 입니다.',
  button: '버튼명 미리보기',
} as const;

const ImagePlaceholderIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox={'0 0 24 24'} fill={'none'} stroke={PLACEHOLDER_FG} strokeWidth={1.5}>
    <rect x={3} y={5} width={18} height={14} rx={2} />
    <circle cx={8.5} cy={10} r={1.5} />
    <path d={'M5 17l5-5 4 4 2-2 3 3'} />
  </svg>
);

const PreviewImage = ({
  src,
  placeholder = false,
  placeholderRatio = '2 / 1',
  fixedRatio,
  compact = false,
}: {
  src?: string;
  placeholder?: boolean;
  placeholderRatio?: string;
  fixedRatio?: string;
  compact?: boolean;
}) =>
  src ? (
    <Image
      html
      src={src}
      alt={''}
      style={{
        aspectRatio: fixedRatio,
        display: 'block',
        height: 'auto',
        objectFit: fixedRatio ? 'cover' : undefined,
        width: '100%',
      }}
    />
  ) : placeholder ? (
    <View
      sx={{
        aspectRatio: placeholderRatio,
        backgroundColor: PLACEHOLDER_BG,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}
    >
      <ImagePlaceholderIcon size={compact ? 20 : 40} />
      {compact ? null : (
        <Text fontSize={'12px'} fontWeight={'bold'} color={PLACEHOLDER_FG}>
          {PREVIEW_PLACEHOLDER.image}
        </Text>
      )}
    </View>
  ) : null;

const Price = ({ commerce }: { commerce?: CommerceValue | null }) => {
  if (!commerce) return null;

  // 고정변수명 조합이면 네 값이 모두 변수명이라 숫자 대신 그대로 보여준다.
  //   저장 후에는 *Name 이 채워져 오고, 작성 중에는 usePriceVariable 만 켜져 있다.
  const isVariable = Boolean(commerce.usePriceVariable || commerce.regularPriceName);
  if (isVariable) {
    // 템플릿에는 네 변수가 다 등록되지만, 실제 노출 조합은 **발송 쿼리의 컬럼**이 정한다.
    //   작성 중(저장 전)에는 아직 쿼리가 없으므로 할인 조합을 기본으로 보여주고,
    //   발송 미리보기에서는 쿼리가 채운 값만 남아 있어 그대로 그린다.
    const composing = !commerce.regularPriceName;
    const hasDiscount = composing || Boolean(commerce.discountPriceName);

    if (!hasDiscount) {
      return (
        <View px={'10px'} py={'2px'}>
          <Text fontSize={'14px'} fontWeight={'bold'}>
            {commerce.regularPriceName}원
          </Text>
        </View>
      );
    }
    return (
      <View px={'10px'} py={'2px'} sx={{ display: 'flex', gap: '6px', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Text fontSize={'14px'} fontWeight={'bold'}>
          {commerce.discountPriceName || COMMERCE_PRICE_VARIABLES.discountPrice}원
        </Text>
        <Text fontSize={'11px'} color={'#86868b'} sx={{ textDecoration: 'line-through' }}>
          {commerce.regularPriceName || COMMERCE_PRICE_VARIABLES.regularPrice}
        </Text>
        <Text fontSize={'11px'} fontWeight={'bold'} color={'#ff9500'}>
          {commerce.discountFixedName
            ? `${commerce.discountFixedName}원↓`
            : `${commerce.discountRateName || COMMERCE_PRICE_VARIABLES.discountRate}%↓`}
        </Text>
      </View>
    );
  }

  const regularPrice = formatPrice(commerce.regularPrice);
  const discountPrice = formatPrice(commerce.discountPrice);
  const discountFixed = formatPrice(commerce.discountFixed);
  const hasDiscountRate = commerce.discountRate != null;
  if (!regularPrice && !discountPrice) return null;

  // 할인가가 있으면 할인가가 대표가가 되고 정상가는 취소선으로 뒤따른다(취소선 가격에는 '원'을 붙이지 않는다).
  // 할인 표기는 할인율(10%↓) 또는 정액할인가격(1,000원↓) 중 하나만 — 둘은 상호 배타다.
  const mainPrice = discountPrice || regularPrice;
  const strikePrice = discountPrice ? regularPrice : null;
  const discountLabel = hasDiscountRate
    ? `${commerce.discountRate}%↓`
    : discountFixed
      ? `${discountFixed}원↓`
      : null;

  return (
    <View px={'10px'} py={'2px'} sx={{ display: 'flex', gap: '6px', alignItems: 'baseline', flexWrap: 'wrap' }}>
      <Text fontSize={'14px'} fontWeight={'bold'}>
        {mainPrice}원
      </Text>
      {strikePrice ? (
        <Text fontSize={'11px'} color={'#86868b'} sx={{ textDecoration: 'line-through' }}>
          {strikePrice}
        </Text>
      ) : null}
      {discountLabel ? (
        <Text fontSize={'11px'} fontWeight={'bold'} color={'#ff9500'}>
          {discountLabel}
        </Text>
      ) : null}
    </View>
  );
};

const ActionButtons = ({
  buttons,
  padding,
  max,
  placeholder = false,
}: {
  buttons?: ReadonlyArray<ButtonValue> | null;
  padding: string;
  max: number;
  /** 버튼이 하나도 없을 때 자리표시 버튼을 보여줄지(빈 템플릿 미리보기용) */
  placeholder?: boolean;
}) => {
  const filled = (buttons ?? []).filter((button) => button.name);
  const isPlaceholder = !filled.length;
  if (isPlaceholder && !placeholder) return null;
  const validButtons = isPlaceholder ? [{ name: PREVIEW_PLACEHOLDER.button }] : filled;
  const sideBySide = validButtons.length === 2 && max === 2;

  return (
    <View
      sx={{
        display: 'flex',
        flexDirection: sideBySide ? 'row' : 'column',
        gap: sideBySide ? '6px' : '4px',
        padding,
      }}
    >
      {validButtons.map((button, index) => (
        <View
          key={`${button.name}-${index}`}
          sx={{
            flex: sideBySide ? 1 : undefined,
            padding: '10px',
            textAlign: 'center',
            backgroundColor: '#f0f0f0',
            borderRadius: '8px',
          }}
        >
          <Text display={'block'} color={isPlaceholder ? PLACEHOLDER_FG : '#333'} fontSize={'13px'} fontWeight={500}>
            {button.name}
          </Text>
        </View>
      ))}
    </View>
  );
};

const Carousel = ({ value }: Props) => {
  const items = value.carousel?.list ?? [];
  const isCommerce = value.chatBubbleType === 'CAROUSEL_COMMERCE';
  const head = value.carousel?.head;
  const cards = [...(head ? [{ ...head, isHead: true }] : []), ...items.map((item) => ({ ...item, isHead: false }))];

  return (
    <View
      width={240}
      sx={{ display: 'flex', gap: '8px', overflowX: 'auto', scrollSnapType: 'x mandatory', paddingRight: '12%' }}
    >
      {cards.map((item, index) => {
        const isHead = item.isHead;
        return (
          <View
            key={index}
            sx={{
              flex: '0 0 85%',
              scrollSnapAlign: 'start',
              overflow: 'hidden',
              backgroundColor: isHead ? '#f0f7ff' : '#fff',
              border: `1px solid ${isHead ? '#cde1fa' : '#e5e5ea'}`,
              borderRadius: '10px',
              boxSizing: 'border-box',
            }}
          >
            <PreviewImage src={assetUrl(item.image, item.imageUrl)} placeholder placeholderRatio={'2 / 1'} />
            {isHead || !isCommerce ? (
              <>
                <Text
                  display={'block'}
                  sx={{ padding: '8px 10px 2px', whiteSpace: 'pre-wrap' }}
                  fontSize={'13px'}
                  fontWeight={'bold'}
                  color={item.header ? undefined : PLACEHOLDER_FG}
                >
                  {item.header || PREVIEW_PLACEHOLDER.header}
                </Text>
                <Text
                  display={'block'}
                  color={item.content ? '#333' : PLACEHOLDER_FG}
                  sx={{ padding: '2px 10px 8px', whiteSpace: 'pre-wrap', maxHeight: '110px', overflow: 'auto' }}
                  fontSize={'12px'}
                >
                  {item.content || PREVIEW_PLACEHOLDER.content}
                </Text>
              </>
            ) : (
              <>
                <Text
                  display={'block'}
                  sx={{ padding: '8px 10px 2px' }}
                  fontSize={'13px'}
                  fontWeight={'bold'}
                  color={item.commerce?.title ? undefined : PLACEHOLDER_FG}
                >
                  {item.commerce?.title || PREVIEW_PLACEHOLDER.commerceTitle}
                </Text>
                <Price commerce={item.commerce} />
                {item.additionalContent ? (
                  <Text
                    display={'block'}
                    color={'#86868b'}
                    sx={{ padding: '2px 10px 6px', whiteSpace: 'pre-wrap' }}
                    fontSize={'11px'}
                  >
                    {item.additionalContent}
                  </Text>
                ) : null}
              </>
            )}
            {!isHead ? <ActionButtons buttons={item.buttons} padding={'0 10px 10px'} max={2} placeholder /> : null}
          </View>
        );
      })}
    </View>
  );
};

const WideItemList = ({ value }: Props) => {
  const items = value.wideItemList ?? [value.mainWideItem, ...(value.subWideItemList ?? [])].filter(Boolean);
  const [main, ...subItems] = items as WideItemValue[];

  return (
    <View width={240} overflow={'hidden'} borderRadius={'12px'} backgroundColor={'#fff'}>
      <Text
        display={'block'}
        sx={{ padding: '8px 10px 4px', textAlign: 'center' }}
        fontSize={'13px'}
        fontWeight={'bold'}
        color={value.header ? undefined : PLACEHOLDER_FG}
      >
        {value.header || PREVIEW_PLACEHOLDER.header}
      </Text>
      {main ? (
        <View sx={{ paddingTop: '10px', borderBottom: '1px solid #eee' }}>
          <View sx={{ margin: '0 10px' }}>
            <PreviewImage
              src={assetUrl(main.image, main.imageUrl)}
              placeholder
              placeholderRatio={'2 / 1'}
              fixedRatio={'2 / 1'}
            />
          </View>
          <Text
            display={'block'}
            sx={{ padding: '6px 10px', whiteSpace: 'pre-wrap' }}
            fontSize={'12px'}
            fontWeight={600}
            color={main.title ? undefined : PLACEHOLDER_FG}
          >
            {main.title || PREVIEW_PLACEHOLDER.itemTitle}
          </Text>
        </View>
      ) : null}
      {subItems.map((item, index) => (
        <View
          key={index}
          sx={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderBottom: '1px solid #eee' }}
        >
          <View width={45} minWidth={45}>
            <PreviewImage
              src={assetUrl(item.image, item.imageUrl)}
              placeholder
              compact
              placeholderRatio={'1 / 1'}
              fixedRatio={'1 / 1'}
            />
          </View>
          <Text
            display={'block'}
            sx={{ paddingLeft: '8px', lineHeight: 1.3, whiteSpace: 'pre-wrap' }}
            fontSize={'12px'}
            color={item.title ? undefined : PLACEHOLDER_FG}
          >
            {item.title || PREVIEW_PLACEHOLDER.itemTitle}
          </Text>
        </View>
      ))}
      <ActionButtons buttons={value.buttons} padding={'8px 10px'} max={2} />
    </View>
  );
};

const SingleBubble = ({ value }: Props) => {
  const type = value.chatBubbleType;
  const isCommerce = type === 'COMMERCE';
  const imageUrl = assetUrl(value.image, value.imageUrl);
  const showImage = ['IMAGE', 'WIDE'].includes(type || '') || isCommerce;
  const videoUrl = value.video?.videoUrl || value.video?.objectUrl;
  const thumbnailUrl = value.video?.thumbnailUrl || '';
  const maxButtons = type === 'TEXT' || type === 'IMAGE' ? 5 : 2;

  return (
    <View width={240} overflow={'hidden'} borderRadius={'12px'} backgroundColor={'#fff'}>
      {showImage ? (
        <PreviewImage src={imageUrl} placeholder placeholderRatio={type === 'WIDE' ? '1 / 1' : '2 / 1'} />
      ) : null}
      {type === 'PREMIUM_VIDEO' ? (
        videoUrl ? (
          <video
            controls
            src={videoUrl}
            poster={thumbnailUrl || undefined}
            style={{ display: 'block', width: '100%' }}
          />
        ) : (
          <View
            sx={{ aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            backgroundColor={'#eee'}
          >
            <Text fontSize={'24px'}>▶</Text>
          </View>
        )
      ) : null}
      {type === 'PREMIUM_VIDEO' && value.header ? (
        <Text display={'block'} sx={{ padding: '8px 10px 2px' }} fontSize={'13px'} fontWeight={'bold'}>
          {value.header}
        </Text>
      ) : null}
      {isCommerce ? (
        <>
          <Text
            display={'block'}
            sx={{ padding: '8px 10px 2px' }}
            fontSize={'13px'}
            fontWeight={600}
            color={value.commerce?.title ? undefined : PLACEHOLDER_FG}
          >
            {value.commerce?.title || PREVIEW_PLACEHOLDER.commerceTitle}
          </Text>
          <Price commerce={value.commerce} />
          {value.additionalContent ? (
            <Text
              display={'block'}
              color={'#86868b'}
              sx={{ padding: '2px 10px 8px', whiteSpace: 'pre-wrap' }}
              fontSize={'11px'}
            >
              {value.additionalContent}
            </Text>
          ) : null}
        </>
      ) : type === 'PREMIUM_VIDEO' && !value.content ? null : (
        <Text
          display={'block'}
          sx={{ padding: '10px 12px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          fontSize={'12px'}
          color={value.content ? undefined : PLACEHOLDER_FG}
        >
          {value.content || PREVIEW_PLACEHOLDER.content}
        </Text>
      )}
      <ActionButtons buttons={value.buttons} padding={'8px 10px'} max={maxButtons} placeholder={isCommerce} />
    </View>
  );
};

const BrandMessageTemplatePreview = ({ value }: Props) => {
  const now = new Date();
  const hour = now.getHours();
  const time = `${hour >= 12 ? '오후' : '오전'} ${hour % 12 || 12}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <View
      width={320}
      minHeight={200}
      marginX={'auto'}
      overflow={'hidden'}
      borderRadius={'16px'}
      backgroundColor={'#b2c7d9'}
    >
      <View
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          borderBottom: '1px solid rgba(0,0,0,.06)',
        }}
      >
        <Text display={'block'} textAlign={'center'} fontSize={'14px'} fontWeight={600} sx={{ flex: 1 }}>
          수학대왕
        </Text>
      </View>
      <View p={'12px'}>
        <View sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
          <Image
            html
            src={PROFILE_IMAGE}
            alt={'수학대왕'}
            width={38}
            height={38}
            style={{
              flexShrink: 0,
              boxSizing: 'border-box',
              objectFit: 'contain',
              padding: '7px',
              borderRadius: '10px',
            }}
          />
          <View minWidth={0}>
            <Text display={'block'} color={'#555'} fontSize={'11px'} sx={{ marginBottom: '3px' }}>
              (광고) 수학대왕
            </Text>
            {value.chatBubbleType === 'WIDE_ITEM_LIST' ? (
              <WideItemList value={value} />
            ) : value.chatBubbleType === 'CAROUSEL_FEED' || value.chatBubbleType === 'CAROUSEL_COMMERCE' ? (
              <Carousel value={value} />
            ) : (
              <SingleBubble value={value} />
            )}
            <Text
              display={'block'}
              color={'#888'}
              fontSize={'10px'}
              textAlign={'right'}
              sx={{ marginTop: '4px', paddingRight: '4px' }}
            >
              {time}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export default BrandMessageTemplatePreview;
